/**
 * Workspaces: the top-level areas of Forge a person switches between from the
 * sidebar header (SEO, Studio, Outreach, Tasks, Events, Products, Support, Admin, Agents). The Admin
 * workspace keeps the internal id `agency`, so stored workspace grants
 * (users.workspaces) and remembered paths keep working. Pure data and
 * rules, no React, so the node tests can pin them.
 *
 * A workspace is derived from the URL, never stored as its own state: every
 * path belongs to exactly one workspace, or to none (settings, search), in
 * which case the previous workspace stays on screen. Someone without access
 * to a workspace never sees it; with only one, there is no switcher at all.
 */

export type WorkspaceId = "seo" | "studio" | "outreach" | "tasks" | "events" | "products" | "support" | "agency" | "agents"

export interface WorkspaceMeta {
  id: WorkspaceId
  label: string
  /** One line under the label in the switcher menu. */
  blurb: string
  /** Where a first visit lands. */
  home: string
}

/** Switcher order. */
export const WORKSPACES: WorkspaceMeta[] = [
  { id: "seo", label: "SEO", blurb: "Client sites, content and links", home: "/" },
  { id: "studio", label: "Studio", blurb: "Creator Studio", home: "/studio" },
  { id: "outreach", label: "Outreach", blurb: "Cold-email queue", home: "/outreach" },
  { id: "tasks", label: "Tasks", blurb: "Tasks, projects and boards", home: "/tasks" },
  { id: "events", label: "Events", blurb: "App events and push", home: "/events" },
  // Our own products' signups, traffic, search and revenue. Owners only.
  { id: "products", label: "Products", blurb: "Our products' numbers", home: "/products" },
  // FOR-350: our products' customer support inbox. The owner, and project members for their projects.
  { id: "support", label: "Support", blurb: "Our products' support inbox", home: "/support" },
  // FOR-326: Cody, the Reviewer and the Sweeper, plus the desk agents. Owners only.
  { id: "agents", label: "Agents", blurb: "Cody, Reviewer and Sweeper", home: "/agents" },
  // Admin stays last.
  { id: "agency", label: "Admin", blurb: "Clients, team and invoices", home: "/admin" },
]

export function workspaceMeta(id: WorkspaceId): WorkspaceMeta {
  return WORKSPACES.find((w) => w.id === id)!
}

const PREFIXES: [string, WorkspaceId][] = [
  ["/studio", "studio"],
  ["/outreach", "outreach"],
  ["/tasks", "tasks"],
  ["/projects", "tasks"],
  ["/events", "events"],
  ["/products", "products"],
  ["/support", "support"],
  ["/admin", "agency"],
  ["/agents", "agents"],
  ["/prompt-log", "agency"],
  ["/invoices", "agency"],
  ["/training", "agency"],
]

/** Pages every workspace shares: they keep whichever workspace was open. */
const NEUTRAL = ["/settings", "/search", "/no-access"]

function underPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + "/")
}

/** The workspace a path belongs to, or null for the shared pages. */
export function workspaceForPath(pathname: string): WorkspaceId | null {
  if (NEUTRAL.some((p) => underPrefix(pathname, p))) return null
  for (const [prefix, id] of PREFIXES) {
    if (underPrefix(pathname, prefix)) return id
  }
  return "seo"
}

/**
 * The workspaces Admin → Users grants one by one. Outreach and Events have
 * their own per-person toggles.
 */
export const GRANTABLE_WORKSPACES: WorkspaceId[] = ["seo", "studio", "tasks", "agency"]

/**
 * Per-admin permissions (API App\Support\AdminPermissions): which admin pages
 * an admin may open. /auth/user `admin_permissions` lists them, every key for
 * the owner; `settings` and `permissions` are the owner's alone. Keep in step
 * with the API registry.
 */
export const ADMIN_PERMISSION_KEYS = [
  "overview", "clients", "licenses", "payroll", "compensation", "client_pulse", "client_dashboards",
  "team_hours", "users", "people_access", "agents", "prompt_log", "ai_visibility", "local_rank", "api_activity",
  "webhooks", "uptime", "site_email", "docs", "feedback", "slack_intake", "github_repositories",
  "newsroom_verticals", "broadcast", "settings", "permissions", "error_tracking", "secret_access", "hq",
] as const

export type AdminPermissionKey = (typeof ADMIN_PERMISSION_KEYS)[number]

/**
 * The keys no admin can be granted (API AdminPermissions owner_only). The API
 * reports them on /auth/user as `owner_only_admin_permissions`; this list is
 * only the fallback for an API that does not yet.
 */
export const OWNER_ONLY_ADMIN_KEYS_FALLBACK: readonly AdminPermissionKey[] = ["settings", "permissions", "error_tracking", "secret_access", "hq"]

export interface WorkspaceAccess {
  isAdmin: boolean
  /** The owner holds every admin permission, whatever the list says. */
  isOwner?: boolean
  /**
   * /auth/user admin_permissions. Fail closed: absent or empty opens no
   * admin page, and it counts only for an admin.
   */
  adminPermissions?: readonly string[] | null
  /** /auth/user owner_only_admin_permissions: keys only the owner holds. */
  ownerOnlyKeys?: readonly string[] | null
  /**
   * /auth/user `workspaces`: null (or absent) is Standard access, the rules
   * below; a list is everything this person gets (the API enforces it too).
   */
  allowed?: WorkspaceId[] | null
  hasLicenses: boolean
  /** /studio/access can_access */
  canStudio: boolean
  /** /studio/access studio_only: Studio is this person's whole Forge. */
  studioOnly: boolean
  /** True until /studio/access answers for a non-admin (they might be Studio-only). */
  studioPending: boolean
  outreachEnabled: boolean
  eventsEnabled: boolean
  /** /auth/user support_member: a member of one or more Support projects. */
  supportMember?: boolean
  promptLogEnabled: boolean
  /**
   * /auth/user has_invoices: this person has submitted an invoice or is an
   * invoice participant. False hides "My invoices" from the sidebar (the
   * owner never invoices); the page itself stays reachable. Unset (an older
   * API) keeps the item.
   */
  hasInvoices?: boolean
}

/**
 * Which Agency pages a person can open. Invoices and Training were in the top
 * bar for every non-Studio-only user before workspaces existed, and the prompt
 * log was granted per person, so a non-admin keeps exactly those.
 */
function granted(a: WorkspaceAccess, id: WorkspaceId): boolean {
  return !a.allowed || a.allowed.includes(id)
}

/** Whether `key` is the owner's alone, from the API's registry (or the fallback list). */
export function isOwnerOnlyKey(a: Pick<WorkspaceAccess, "ownerOnlyKeys">, key: AdminPermissionKey): boolean {
  return (a.ownerOnlyKeys ?? OWNER_ONLY_ADMIN_KEYS_FALLBACK).includes(key)
}

/**
 * Whether this admin may open the admin pages behind `key`. Never true for a
 * non-admin, and an owner-only key never counts for an admin (as on the API,
 * which drops one written into a list by hand).
 */
export function hasAdminPermission(a: Pick<WorkspaceAccess, "isAdmin" | "isOwner" | "adminPermissions" | "ownerOnlyKeys">, key: AdminPermissionKey): boolean {
  if (a.isOwner) return true
  return a.isAdmin && !isOwnerOnlyKey(a, key) && (a.adminPermissions ?? []).includes(key)
}

export function agencyItems(a: WorkspaceAccess) {
  const basics = !a.studioOnly && !a.studioPending && granted(a, "agency")
  return {
    // The /admin pages: an admin with licences, as the Admin link always was,
    // holding at least one admin-page permission.
    admin: a.isAdmin && a.hasLicenses && ADMIN_PAGE_KEYS().some((key) => hasAdminPermission(a, key)),
    // /auth/user already answers prompt_log_enabled per key for an admin.
    promptLog: a.promptLogEnabled || hasAdminPermission(a, "prompt_log"),
    invoices: basics,
    training: basics,
    // HQ, the infrastructure map: the owner alone, never by permission.
    owner: !!a.isOwner,
  }
}

export type AgencyGate = keyof ReturnType<typeof agencyItems>

export interface AgencyNavItem {
  /** Stable id; the sidebar maps it to an icon. */
  id: string
  label: string
  to: string
  gate: AgencyGate
  /** Match `to` exactly instead of as a prefix (the /admin overview). */
  exact?: boolean
  /** More path prefixes this item owns, e.g. detail pages linked from it. */
  also?: string[]
  /** The admin permission the page needs (owner = all). */
  permission?: AdminPermissionKey
  /** Set by agencyNav: only the owner can open it (its permission is owner-only). */
  ownerOnly?: boolean
}

export interface AgencyNavGroup {
  id: string
  title: string
  items: AgencyNavItem[]
}

/**
 * The Admin sidebar: every admin page, plus the Admin-workspace pages a teammate may
 * have. Each destination appears once. This replaced the tab strip that sat
 * on top of every /admin page, so every /admin route in App.tsx must be
 * reachable from here, apart from redirects and the pages listed in
 * OFF_SIDEBAR (tests/workspaces.test.mjs checks that).
 */
/** Admin pages deliberately reached from somewhere other than the sidebar. */
export const OFF_SIDEBAR = ["/admin/feedback"]

export const AGENCY_NAV: AgencyNavGroup[] = [
  {
    id: "overview",
    title: "Overview",
    items: [
      { id: "hq", label: "HQ", to: "/admin/hq", gate: "owner", permission: "hq" },
      { id: "overview", label: "Overview", to: "/admin", gate: "admin", exact: true, permission: "overview" },
      { id: "health", label: "Health", to: "/admin/health", gate: "admin", permission: "overview" },
    ],
  },
  {
    id: "clients",
    title: "Clients & billing",
    items: [
      // Site detail pages are opened from the overview and from projects and
      // agents, but they are a client's site, so Clients owns them.
      { id: "clients", label: "Clients", to: "/admin/clients", gate: "admin", also: ["/admin/sites"], permission: "clients" },
      { id: "licenses", label: "Licenses", to: "/admin/licenses", gate: "admin", permission: "licenses" },
      // Every team payment, Forge invoices and HireTalent.ph alike; the old
      // Invoices page is its Forge invoices tab.
      { id: "payroll", label: "Payroll", to: "/admin/payroll", gate: "admin", also: ["/admin/invoices"], permission: "payroll" },
      { id: "client-pulse", label: "Weekly pulse", to: "/admin/client-pulse", gate: "admin", permission: "client_pulse" },
      { id: "client-dashboards", label: "Client dashboards", to: "/admin/client-dashboards", gate: "admin", permission: "client_dashboards" },
      // The person's own invoices (submit, download). Not the admin list above.
      { id: "my-invoices", label: "My invoices", to: "/invoices", gate: "invoices" },
    ],
  },
  {
    id: "people",
    title: "People",
    items: [
      { id: "team", label: "Team hours", to: "/admin/team", gate: "admin", permission: "team_hours" },
      // What each person is marked as earning (not Payroll, which is what was billed).
      { id: "compensation", label: "Compensation", to: "/admin/compensation", gate: "admin", permission: "compensation" },
      // Roles (what each person owns) is a tab of Users.
      { id: "users", label: "Users", to: "/admin/users", gate: "admin", permission: "users" },
      // Who works which product's support inbox: Support → Projects → People (the owner's).
      { id: "support-people", label: "Support access", to: "/support/projects", gate: "owner" },
    ],
  },
  {
    id: "ai",
    title: "AI",
    items: [
      // One prompt log, two URLs: admins use /admin/prompt-log, the per-person
      // grant uses /prompt-log. agencyNav picks the one this person has.
      { id: "prompts", label: "Prompts", to: "/admin/prompt-log", gate: "promptLog", also: ["/prompt-log"] },
      { id: "ai-visibility", label: "AI Visibility", to: "/admin/ai-visibility", gate: "admin", permission: "ai_visibility" },
      { id: "local-rank", label: "Local Rank", to: "/admin/local-rank", gate: "admin", permission: "local_rank" },
      // Only calls made with a personal token (the CLI, MCP, scripts) are logged here.
      { id: "api-activity", label: "Token activity", to: "/admin/api-activity", gate: "admin", permission: "api_activity" },
      { id: "webhooks", label: "Webhooks", to: "/admin/webhooks", gate: "admin", permission: "webhooks" },
    ],
  },
  {
    id: "product",
    title: "Product",
    items: [
      // Feedback is opened from the FOR board, where the reports land as tasks.
      { id: "training", label: "Training", to: "/training", gate: "training" },
      // Server vitals (the old Infrastructure page) are Uptime's Servers tab.
      { id: "uptime", label: "Uptime", to: "/admin/uptime", gate: "admin", permission: "uptime" },
      // PostHog error tracking beside Sentry per app: the owner alone (it reads with his PostHog key).
      { id: "error-tracking", label: "Error tracking", to: "/admin/error-tracking", gate: "owner", permission: "error_tracking" },
      // Every 1Password CLI read on the agent box, shipped off the box each minute: the owner alone.
      { id: "secret-access", label: "Secret access", to: "/admin/secret-access", gate: "owner", permission: "secret_access" },
      // Whether each client site can still send email (daily test email).
      { id: "site-email", label: "Site email", to: "/admin/site-email", gate: "admin", permission: "site_email" },
      { id: "docs", label: "Docs", to: "/admin/docs", gate: "admin", permission: "docs" },
      { id: "settings", label: "Settings", to: "/admin/settings", gate: "admin", permission: "settings" },
    ],
  },
]

/**
 * The Admin sidebar this person sees: items they cannot open are dropped,
 * and My invoices for someone who never invoices, then any group left empty. A teammate's own invoices are just "Invoices",
 * since they have no admin list to tell them apart from.
 */
export function agencyNav(a: WorkspaceAccess): AgencyNavGroup[] {
  const items = agencyItems(a)
  return AGENCY_NAV.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => items[item.gate])
      .filter((item) => !item.permission || hasAdminPermission(a, item.permission))
      .filter((item) => !(item.id === "my-invoices" && a.hasInvoices === false))
      .map((item) => ({ ...item, ownerOnly: !!item.permission && isOwnerOnlyKey(a, item.permission) }))
      .map((item) => {
        if (item.id === "prompts" && !a.isAdmin) return { ...item, to: "/prompt-log" }
        // "My" only beside the admin list of everyone's (Payroll).
        if (item.id === "my-invoices" && !(items.admin && hasAdminPermission(a, "payroll"))) return { ...item, label: "Invoices" }
        return item
      }),
  })).filter((group) => group.items.length > 0)
}

/** The keys the /admin pages (gate "admin") need, for "has any admin page". */
function ADMIN_PAGE_KEYS(): AdminPermissionKey[] {
  return AGENCY_NAV.flatMap((g) => g.items).filter((i) => i.gate === "admin" && i.permission).map((i) => i.permission!)
}

/** Admin routes that are not sidebar items, and the permission each needs. */
const EXTRA_ROUTE_PERMISSIONS: [string, AdminPermissionKey][] = [
  ["/admin/feedback", "feedback"],
  // The Prompts item is gated by promptLog (a teammate's grant uses /prompt-log).
  ["/admin/prompt-log", "prompt_log"],
  ["/admin/roles", "users"],
  ["/admin/people-access", "people_access"],
  ["/admin/goals", "overview"],
  ["/admin/usage", "overview"],
  ["/admin/infrastructure", "uptime"],
]

/**
 * Admin pages only the owner may open: the sidebar items whose permission is
 * owner-only. No admin permission grants them.
 */
export const OWNER_ONLY_PATHS = AGENCY_NAV.flatMap((g) => g.items)
  .filter((i) => i.permission && OWNER_ONLY_ADMIN_KEYS_FALLBACK.includes(i.permission))
  .map((i) => i.to)

export function isOwnerOnlyPath(pathname: string): boolean {
  return OWNER_ONLY_PATHS.some((p) => underPrefix(pathname.replace(/\/+$/, ""), p))
}

/**
 * The admin permission a path needs, from the sidebar (an item's `to` and
 * `also`) plus the off-sidebar routes; the longest prefix wins, and /admin
 * itself is the overview. Null for a path that needs none (or no route).
 */
export function adminPermissionForPath(pathname: string): AdminPermissionKey | null {
  if (pathname === "/admin" || pathname === "/admin/") return "overview"
  let best: { key: AdminPermissionKey; length: number } | null = null
  const consider = (prefix: string, key: AdminPermissionKey) => {
    if (prefix !== "/admin" && underPrefix(pathname, prefix) && (!best || prefix.length > best.length)) best = { key, length: prefix.length }
  }
  for (const item of AGENCY_NAV.flatMap((g) => g.items)) {
    if (!item.permission) continue
    for (const prefix of [item.to, ...(item.also ?? [])]) consider(prefix, item.permission)
  }
  for (const [prefix, key] of EXTRA_ROUTE_PERMISSIONS) consider(prefix, key)
  return (best as { key: AdminPermissionKey } | null)?.key ?? null
}

/** Whether this admin may open an /admin path (the API refuses the rest anyway). */
export function canOpenAdminPath(pathname: string, a: WorkspaceAccess): boolean {
  if (isOwnerOnlyPath(pathname)) return !!a.isOwner
  const key = adminPermissionForPath(pathname)
  return key ? hasAdminPermission(a, key) : a.isAdmin
}

/**
 * The item a path highlights: the longest matching prefix wins, so
 * /admin/sites/4 lights Clients and /admin/users lights Users, not Overview.
 */
export function agencyActiveItem(pathname: string, groups: AgencyNavGroup[] = AGENCY_NAV): string | null {
  let best: { id: string; length: number } | null = null
  for (const group of groups) {
    for (const item of group.items) {
      const prefixes = [item.to, ...(item.also ?? [])]
      for (const prefix of prefixes) {
        const hit = item.exact && prefix === item.to ? pathname === prefix : underPrefix(pathname, prefix)
        if (hit && (!best || prefix.length > best.length)) best = { id: item.id, length: prefix.length }
      }
    }
  }
  return best?.id ?? null
}

/** The workspaces this person can see, in switcher order. */
export function availableWorkspaces(a: WorkspaceAccess): WorkspaceId[] {
  if (a.studioOnly) return ["studio"]
  const agency = agencyItems(a)
  const can: Record<WorkspaceId, boolean> = {
    seo: a.hasLicenses && !a.studioPending && granted(a, "seo"),
    // /studio/access already applies a custom list.
    studio: a.canStudio,
    outreach: a.outreachEnabled,
    tasks: !a.studioPending && granted(a, "tasks"),
    // The owner, or the per-person toggle; being an admin is not enough.
    events: a.eventsEnabled,
    agency: agency.admin || agency.promptLog || agency.invoices || agency.training,
    // The agents workspace (FOR-326) is the owner's; the API gates it the same way.
    agents: !!a.isOwner,
    // Products is the owner's too; admins never see it (the API's `owner` gate).
    products: !!a.isOwner,
    // Support (FOR-350) is the owner's, plus project members for their own
    // projects (the API scopes /support/member to them).
    support: !!a.isOwner || !!a.supportMember,
  }
  return WORKSPACES.map((w) => w.id).filter((id) => can[id])
}

/**
 * Where to send someone on a path whose workspace they do not have: null when
 * the path is fine (its workspace is available, or it is a shared page).
 */
export function redirectForPath(pathname: string, available: WorkspaceId[], a: WorkspaceAccess): string | null {
  const own = workspaceForPath(pathname)
  if (!own || available.includes(own)) return null
  const first = available[0]
  if (!first) return "/no-access"
  return first === "agency" ? agencyHome(a) : workspaceMeta(first).home
}

/**
 * The workspace to show for a path: the path's own, or for a shared page the
 * one that was open, or else the first one this person has.
 */
export function resolveWorkspace(
  pathname: string,
  previous: WorkspaceId | null,
  available: WorkspaceId[],
): WorkspaceId {
  const own = workspaceForPath(pathname)
  if (own) return own
  if (previous) return previous
  return available[0] ?? "seo"
}

/**
 * Where an address no route knows goes: that workspace's own home, so a dead
 * link stays in its workspace. Sending everything to / (SEO) also left a
 * removed page remembered as "last Agency page", and every switch to Agency
 * then bounced to SEO (/admin/incomplete-features, removed 2026-10).
 */
export function unknownPathFallback(pathname: string, a: WorkspaceAccess): string {
  const own = workspaceForPath(pathname)
  const home = !own || own === "seo" ? "/" : own === "agency" ? agencyHome(a) : workspaceMeta(own).home
  return home === pathname ? "/" : home
}

/** The first page of the Admin workspace someone can open: their first permitted admin page, else the teammate pages. */
export function agencyHome(a: WorkspaceAccess): string {
  const items = agencyItems(a)
  if (items.admin) {
    const first = AGENCY_NAV.flatMap((g) => g.items).find((i) => i.gate === "admin" && i.permission && hasAdminPermission(a, i.permission))
    if (first) return first.to
  }
  if (items.promptLog) return "/prompt-log"
  if (items.invoices) return "/invoices"
  return "/training"
}

const LAST_PATH_PREFIX = "forge-workspace-last:"

/** Remembers where someone was in each workspace, so switching back returns there. */
export function rememberPath(id: WorkspaceId, path: string, storage: Pick<Storage, "setItem"> | null) {
  try {
    storage?.setItem(LAST_PATH_PREFIX + id, path)
  } catch {
    // Storage blocked (private mode): switching just lands on the home page.
  }
}

export function lastPath(id: WorkspaceId, storage: Pick<Storage, "getItem"> | null): string | null {
  try {
    const value = storage?.getItem(LAST_PATH_PREFIX + id) ?? null
    // Only trust a path that still belongs to this workspace.
    if (value && value.startsWith("/") && workspaceForPath(value.split(/[?#]/)[0]) === id) return value
  } catch {
    // fall through
  }
  return null
}

/** Where switching to a workspace lands. */
export function switchDestination(
  id: WorkspaceId,
  access: WorkspaceAccess,
  storage: Pick<Storage, "getItem"> | null,
): string {
  const remembered = lastPath(id, storage)
  if (remembered) {
    if (id !== "agency") return remembered
    // A remembered Agency page someone lost access to falls back to one they have.
    const items = agencyItems(access)
    const path = remembered.split(/[?#]/)[0]
    const allowed =
      (underPrefix(path, "/admin") && items.admin && canOpenAdminPath(path, access)) ||
      (underPrefix(path, "/prompt-log") && items.promptLog) ||
      (underPrefix(path, "/invoices") && items.invoices) ||
      (underPrefix(path, "/training") && items.training)
    if (allowed) return remembered
  }
  return id === "agency" ? agencyHome(access) : workspaceMeta(id).home
}
